import React, { useEffect, useRef, useState } from "react";
import useWebsocketChat from "./useWebsocketChat";

const WS_URL =
  process.env.REACT_APP_BACKEND_WS_URL || "ws://localhost:8000/ws/chat";
const REST_URL =
  process.env.REACT_APP_BACKEND_REST_URL || "http://localhost:8000/api/chat";

export default function ChatWindow() {
  const { connected, sessionId, messages, partial, sendPrompt } =
    useWebsocketChat(WS_URL);
  const [input, setInput] = useState("");
  const listRef = useRef();

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, partial]);

  const handleSend = async () => {
    if (!input.trim()) return;
    // send via websocket if connected (gives streaming)
    if (connected) {
      sendPrompt(input.trim());
    } else {
      // fallback to REST
      const res = await fetch(REST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: input.trim(), session_id: sessionId }),
      });
      const data = await res.json();
      // append to messages (we could do this via state in the hook; using simple approach)
      // send a "manual" event by calling sendPrompt? here we just ignore streaming
      // For simplicity, refresh by reloading page or rely on websocket for best UX
      console.log("REST response", data);
    }
    setInput("");
  };

  return (
    <div className="chat-window">
      <div className="chat-meta">
        <div className="session-info">
          <span className="label">Session:</span>
          <span className="value">{sessionId || "—"}</span>
        </div>
        <div
          className={`status-badge ${connected ? "connected" : "disconnected"}`}
        >
          <span className="status-dot"></span>
          {connected ? "Connected" : "Disconnected (REST fallback)"}
        </div>
      </div>

      <div className="chat-list" ref={listRef}>
        {messages.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">💬</span>
            <p>No messages yet. Start a conversation!</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`message-group ${m.role}`}>
            <div className="message-avatar">
              {m.role === "user" ? "👤" : "🤖"}
            </div>
            <div className={`message-bubble ${m.role}`}>
              <div className="message-header">
                <span className="role">
                  {m.role === "user" ? "You" : "Assistant"}
                </span>
                {m.timestamp && (
                  <span className="timestamp">
                    {new Date(m.timestamp).toLocaleTimeString()}
                  </span>
                )}
              </div>
              <div className="message-text">{m.text}</div>
            </div>
          </div>
        ))}
        {partial && (
          <div className="message-group assistant streaming">
            <div className="message-avatar">🤖</div>
            <div className="message-bubble assistant streaming">
              <div className="message-header">
                <span className="role">Assistant</span>
                <span className="streaming-indicator">typing</span>
              </div>
              <div className="message-text">{partial}</div>
            </div>
          </div>
        )}
      </div>

      <div className="chat-input-container">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
          rows={1}
          className="chat-textarea"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim()}
          className="send-button"
        >
          <span className="send-icon">📤</span>
          Send
        </button>
      </div>

      {/* New Footer with GitHub Icons */}
      <div className="chat-footer">
        <div className="footer-links">
          <a
            href="https://github.com/Mohammad9473"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
            aria-label="GitHub Profile"
          >
            <svg
              className="github-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
            >
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.113.82-.26.82-.58 0-.287-.01-1.05-.015-2.06-3.338.726-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.09-.745.082-.73.082-.73 1.205.085 1.838 1.237 1.838 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.418-1.305.762-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.468-2.38 1.235-3.22-.123-.3-.535-1.52.117-3.16 0 0 1.008-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.29-1.552 3.297-1.23 3.297-1.23.653 1.64.24 2.86.118 3.16.768.84 1.233 1.91 1.233 3.22 0 4.61-2.804 5.62-5.476 5.92.43.37.824 1.102.824 2.22 0 1.602-.015 2.894-.015 3.287 0 .322.216.698.83.578C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
            </svg>
            <span>GitHub</span>
          </a>

          <a
            href="https://www.linkedin.com/in/mohammadreza-arabameri-a1240561/"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
            aria-label="LinkedIn"
          >
            <svg
              className="linkedin-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="currentColor"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.227 0 22.225 0z" />
            </svg>
            <span>LinkedIn</span>
          </a>
        </div>

        <div className="footer-copyright">
          <span>© {new Date().getFullYear()} Reza</span>
        </div>
      </div>
    </div>
  );
}
